import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import branding from 'shared/app/model/branding';
import { useCallbacks } from 'shared/hooks/utils';
import { useTimer } from 'console/react-elements/timer';
import { isMobileApp } from 'webui/utils/version';
import { Cancel, Repeat } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { useNavigateRoot } from 'shared/hooks/router';
import { fixEspTouch } from 'console/smart-config/components/sending/fix-esp-touch';
export const SmartConfigSending = memo(function SmartConfigSending(props) {
    const { onSaveConfig, name, password, onCancel } = props;
    const [repeat_showed, hideRepeat, showRepeat] = useBooleanState(false);
    const [Timer, startTimer, stopTimer] = useTimer();
    const [message, setMessage] = useState(null);
    const [device_ip, setDeviceIp] = useState(null);
    const startESP = useCallback(async () => {
        setMessage(null);
        hideRepeat();
        if (name === undefined || password === undefined) {
            setMessage(['Настройки не переданы', false]);
            return;
        }
        if (isMobileApp()) {
            startTimer();
            try {
                await new Promise((resolve, reject) => fixEspTouch(name, '00:00:00:00:00:00', password, 1, '0', (suc) => {
                    console.log('SmartConfigSending success', suc);
                    setMessage(['Устройство успешно настроено', true]);
                    setDeviceIp(suc.ip);
                    onSaveConfig?.(name, password);
                    resolve();
                    stopTimer();
                }, (er) => {
                    console.log('SmartConfigSending error', er);
                    setMessage([
                        'Устройство не найдено или уже подключено, пытаемся ещё раз',
                        false,
                    ]);
                    reject();
                    startESP();
                }))
                    .then(async (success) => {
                    console.log('fixEspTouch success', success);
                })
                    .catch((error) => {
                    console.log('fixEspTouch error', error);
                });
            }
            catch (e) {
                console.error('FAILED fixEspTouch', e);
            }
        }
        return;
    }, [setMessage, onSaveConfig, name, password]);
    const lowLevelStop = useCallback(async () => {
        return new Promise((resolve, reject) => {
            if (!isMobileApp()) {
                reject('Not mobile app');
                return;
            }
            window.esptouch.stop((r) => {
                console.log('smartconfig stop ok', r);
                resolve();
            }, (e) => {
                console.log('error on smartconfig stop', e);
                reject();
            });
        });
    }, []);
    const stopESP = useCallback(async () => {
        // console.log('stop');
        setMessage(null);
        try {
            await lowLevelStop();
            stopTimer();
            setMessage(['Настройка отменена', false]);
        }
        catch (error) {
            setMessage([error?.toString() ?? 'Настройка не удалась', false]);
        }
        finally {
            // notDoing();
        }
    }, [setMessage, lowLevelStop]);
    useEffect(() => {
        startESP();
        return () => {
            stopESP().then();
        };
    }, []);
    const onClickCancel = useCallbacks([stopESP, onCancel]);
    const onEndTimer = useCallback(() => {
        showRepeat();
        stopTimer();
        setMessage(['Время вышло...', false]);
    }, []);
    const success = useMemo(() => message && message[1], [message]);
    const onExit = useNavigateRoot();
    return (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, justifyContent: "center", p: 2, children: [_jsx(Typography, { variant: "h5", color: "secondary", children: "\u041F\u0435\u0440\u0435\u0434\u0430\u0447\u0430 \u043A\u043E\u043D\u0444\u0438\u0433\u0443\u0440\u0430\u0446\u0438\u0438 Wi\u2011Fi..." }), _jsxs(Alert, { severity: "warning", children: ["\u0423\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E ", _jsx("strong", { children: branding.name }), " \u043E\u0436\u0438\u0434\u0430\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0435 \u0434\u043B\u044F \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 Wi-Fi \u0432 \u0442\u0435\u0447\u0435\u043D\u0438\u0438 ", _jsx("u", { children: "2\u00A0\u043C\u0438\u043D\u0443\u0442" }), " \u043F\u043E\u0441\u043B\u0435 \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043F\u0438\u0442\u0430\u043D\u0438\u044F."] }), _jsxs(Typography, { variant: "body1", children: ["\u0414\u043B\u044F \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043D\u0435\u043E\u0431\u0445\u043E\u0434\u0438\u043C\u043E \u0440\u0430\u0437\u043C\u0435\u0441\u0442\u0438\u0442\u044C \u043F\u0440\u0438\u0431\u043E\u0440", ' ', _jsx("strong", { children: branding.name }), " \u0432 \u043D\u0435\u043F\u043E\u0441\u0440\u0435\u0434\u0441\u0442\u0432\u0435\u043D\u043D\u043E\u0439 \u0431\u043B\u0438\u0437\u043E\u0441\u0442\u0438 \u043E\u0442 \u0432\u0430\u0448\u0435\u0433\u043E \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430 \u0438\u043B\u0438 \u043F\u043B\u0430\u043D\u0448\u0435\u0442\u0430."] }), _jsx(Typography, { variant: "body1", children: "\u0423\u00A0Wi\u2011Fi-\u0442\u043E\u0447\u043A\u0438 \u0434\u043E\u043B\u0436\u0435\u043D \u0431\u044B\u0442\u044C \u0434\u043E\u0441\u0442\u0443\u043F \u0432\u00A0\u0438\u043D\u0442\u0435\u0440\u043D\u0435\u0442." }), message && (_jsxs(Alert, { severity: success ? 'success' : 'error', children: [device_ip && _jsxs(AlertTitle, { children: ["IP \u043F\u0440\u0438\u0431\u043E\u0440\u0430: ", device_ip] }), message[0]] })), _jsx(Timer, { start_value: 60, interval: 1e3, onEnd: onEndTimer }), !success && (_jsx(LoadingButton, { color: "info", variant: "contained", onClick: startESP, startIcon: _jsx(Repeat, {}), loading: !repeat_showed, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })), success && (_jsx(Button, { color: "success", variant: "contained", onClick: onExit, children: "\u0412\u044B\u0439\u0442\u0438" })), _jsx(Button, { color: "error", variant: "contained", onClick: onClickCancel, startIcon: _jsx(Cancel, {}), children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] }));
});
